/**
 * useIntegrationConnect — the shared "click Connect → run the right
 * connect flow → refetch status when it completes" engine.
 *
 * This is the EXACT logic the marketplace detail modal hand-rolled inline
 * (per-provider OAuth popups, GitLab/Lark form modals, a `refreshKey` that
 * bumps on completion, and a window `message` listener that catches the
 * OAuth popup's postMessage). It's lifted out here so any surface that
 * needs the same connect/connected affordance — the marketplace modal AND
 * now the pipeline Settings tab — drives off one implementation instead of
 * two that drift.
 *
 * The marketplace modal is intentionally NOT refactored to consume this
 * (another agent owns it); this hook is shaped so it COULD adopt it later
 * with no behavior change.
 *
 * Returns:
 *   refreshKey        — bump-on-connect counter; pass into your status
 *                       fetch's dep array so a successful Connect refetches.
 *   handleConnectClick(providerId, fallbackPath)
 *                     — dispatches the click to the right flow:
 *                         github/jira/slack/sentry/notion → OAuth popup
 *                         gitlab                          → GitlabConnectModal
 *                         lark                            → LarkConnectModal
 *                         anything else                   → navigate to
 *                                                           fallbackPath
 *   connectModals     — render this once near your component root; it mounts
 *                       the Gitlab + Lark form modals wired to bump
 *                       refreshKey on success.
 *
 * Usage:
 *   const { refreshKey, handleConnectClick, connectModals } = useIntegrationConnect();
 *   // ...fetch status, include refreshKey in the effect deps...
 *   <button onClick={() => handleConnectClick(id, meta.connectPath)}>Connect</button>
 *   {connectModals}
 */

import React, { useCallback, useEffect, useState } from 'react';
import GitlabConnectModal from '../components/integrations/GitlabConnectModal';
import LarkConnectModal from '../components/integrations/LarkConnectModal';
import { useStartOAuth } from './useIntegrations';

// Marker the popup-done page posts back to the opener after OAuth
// completes. Same constants the marketplace modal uses — kept in sync so
// either surface catches the popup's postMessage.
const OAUTH_POPUP_DONE_PATH = '/oauth-popup-done.html';
const OAUTH_SUCCESS_EVENT = 'zibby:oauth-success';

export default function useIntegrationConnect() {
  // Bumped after any successful Connect (OAuth popup OR a Gitlab/Lark form
  // submit) so the caller's status fetch re-runs. Cheaper + clearer than
  // threading a refetch callback around — same pattern as the modal.
  const [refreshKey, setRefreshKey] = useState(0);
  const [gitlabModalOpen, setGitlabModalOpen] = useState(false);
  const [larkModalOpen, setLarkModalOpen] = useState(false);

  // Per-provider OAuth-start mutations. Each is a thin wrapper around
  // `GET /<provider>/oauth/start?returnTo=…`; mutate() returns
  // { authUrl } that we open in a popup.
  const startGithubOAuth = useStartOAuth('github');
  const startJiraOAuth = useStartOAuth('jira');
  const startSlackOAuth = useStartOAuth('slack');
  const startSentryOAuth = useStartOAuth('sentry');
  const startNotionOAuth = useStartOAuth('notion');
  const OAUTH_HOOKS = {
    github: startGithubOAuth,
    jira: startJiraOAuth,
    slack: startSlackOAuth,
    sentry: startSentryOAuth,
    notion: startNotionOAuth,
  };

  // Listen for the popup-done page postMessage and refetch status. The
  // popup-done page posts to `window.location.origin`, so cross-origin
  // messages are dropped by the browser regardless of this filter.
  useEffect(() => {
    const onMessage = (e) => {
      if (e?.data?.type === OAUTH_SUCCESS_EVENT) {
        setRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Dispatch a Connect click to the right flow per provider. Mirrors the
  // marketplace modal's handleConnectClick 1:1.
  const handleConnectClick = useCallback((providerId, fallbackPath) => {
    if (providerId === 'gitlab') {
      setGitlabModalOpen(true);
      return;
    }
    if (providerId === 'lark') {
      setLarkModalOpen(true);
      return;
    }
    const hook = OAUTH_HOOKS[providerId];
    if (!hook) {
      // Unknown provider — preserve the legacy behavior of navigating to
      // the canonical /integrations page (or whatever connectPath the
      // backend supplied).
      if (fallbackPath) window.location.href = fallbackPath;
      return;
    }
    hook.mutate(
      { returnTo: OAUTH_POPUP_DONE_PATH },
      {
        onSuccess: (data) => {
          // Open the OAuth URL in a small popup so the originating surface
          // stays visible behind it. Popup posts back when done. Fallback
          // to same-tab redirect when the browser blocks the popup.
          const popup = window.open(
            data.authUrl,
            'zibby-oauth',
            'popup=1,width=640,height=720,scrollbars=yes',
          );
          if (!popup) {
            window.location.href = data.authUrl;
          }
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error(`[integrations] Failed to start ${providerId} OAuth:`, err);
        },
      },
    );
  // OAUTH_HOOKS isn't a stable reference but the mutate functions inside it
  // are (react-query memoizes per service); fine for the intent here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The form-based Connect modals, pre-wired to bump refreshKey on success.
  // Render once near the consumer's root. IonModal portals to <body> so
  // placement in the tree doesn't matter.
  const connectModals = (
    <>
      <GitlabConnectModal
        isOpen={gitlabModalOpen}
        onClose={() => setGitlabModalOpen(false)}
        onConnected={() => setRefreshKey((k) => k + 1)}
      />
      <LarkConnectModal
        isOpen={larkModalOpen}
        onClose={() => setLarkModalOpen(false)}
        onConnected={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );

  return { refreshKey, handleConnectClick, connectModals };
}
